import { useState } from "react";
import { Box, Typography, Grid, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Plus, Check, Trash2, ChevronDown, User } from "lucide-react";

import { Button } from "../../components/Button";
import { CustomTextField } from "../../components/CustomTextField";
import { CustomTextArea } from "../../components/CustomTextArea";
import { Label } from "../../components/Label";
import { Separator } from "../../components/Separator";
import { useTheme as useCustomTheme } from "../../theme/useTheme";

export const EMPTY_PERSONA = {
  name: "",
  age: "",
  interests: "",
  painPoints: "",
  goals: "",
};

export const fromBackendPersonas = (personas = []) =>
  personas.map((persona) => ({
    name: persona.name || "",
    age: persona.age?.toString() || "",
    interests: persona.interests || "",
    painPoints: persona.pain_points || "",
    goals: persona.objectives || "",
  }));

export const toBackendPersonas = (personas) =>
  personas.map((persona) => ({
    name: persona.name,
    age: parseInt(persona.age) || null,
    interests: persona.interests,
    pain_points: persona.painPoints,
    objectives: persona.goals,
  }));

export const isPersonaValid = (persona) =>
  Boolean(persona.name?.trim() && persona.interests?.trim());

function PersonaFields({ idPrefix, persona, onFieldChange }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Grid container spacing={1.5}>
        <Grid item xs={8}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Label htmlFor={`${idPrefix}-name`}>
              Nombre <span style={{ color: "red" }}>*</span>
            </Label>
            <CustomTextField
              id={`${idPrefix}-name`}
              placeholder="Ej: María Empresaria"
              value={persona.name}
              onChange={(e) => onFieldChange("name", e.target.value)}
            />
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Label htmlFor={`${idPrefix}-age`}>Edad</Label>
            <CustomTextField
              id={`${idPrefix}-age`}
              placeholder="Ej: 35"
              value={persona.age}
              onChange={(e) => onFieldChange("age", e.target.value)}
            />
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Label htmlFor={`${idPrefix}-interests`}>
          Intereses <span style={{ color: "red" }}>*</span>
        </Label>
        <CustomTextField
          id={`${idPrefix}-interests`}
          placeholder="Ej: Tecnología, productividad, emprendimiento..."
          value={persona.interests}
          onChange={(e) => onFieldChange("interests", e.target.value)}
          multiline
          rows={2}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Label htmlFor={`${idPrefix}-pain`}>Puntos de Dolor</Label>
        <CustomTextArea
          id={`${idPrefix}-pain`}
          placeholder="Ej: Falta de tiempo, presupuesto limitado..."
          value={persona.painPoints}
          onChange={(e) => onFieldChange("painPoints", e.target.value)}
          autoExpand
          maxRows={4}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Label htmlFor={`${idPrefix}-goals`}>Objetivos</Label>
        <CustomTextArea
          id={`${idPrefix}-goals`}
          placeholder="Ej: Hacer crecer su negocio, mejorar eficiencia..."
          value={persona.goals}
          onChange={(e) => onFieldChange("goals", e.target.value)}
          autoExpand
          maxRows={4}
        />
      </Box>
    </Box>
  );
}

/** Controlled buyer persona list: add, edit inline and remove. Saving is up to the parent. */
export function BuyerPersonasEditor({ personas, onChange, disabled = false }) {
  const { theme, colors } = useCustomTheme();
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [draft, setDraft] = useState(null);

  const updatePersona = (index, field, value) =>
    onChange(personas.map((persona, i) => (i === index ? { ...persona, [field]: value } : persona)));

  const removePersona = (index) => {
    onChange(personas.filter((_, i) => i !== index));
    setExpandedIndex(null);
  };

  const confirmDraft = () => {
    if (!isPersonaValid(draft)) return;
    onChange([...personas, draft]);
    setDraft(null);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {personas.map((persona, index) => {
        const isExpanded = expandedIndex === index;
        return (
          <Box
            key={index}
            sx={{
              borderRadius: "8px",
              border: `1px solid ${
                isPersonaValid(persona) ? theme.palette.divider : theme.palette.error.main
              }`,
              backgroundColor: theme.palette.background.paper,
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => setExpandedIndex(isExpanded ? null : index)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                cursor: "pointer",
                "&:hover": { backgroundColor: theme.palette.action.hover },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    backgroundColor: colors.primary.main + "20",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <User size={14} color={colors.primary.main} />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {persona.name || `Persona ${index + 1}`}
                  </Typography>
                  {persona.age && (
                    <Typography variant="caption" color="text.secondary">
                      {persona.age} años
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {!disabled && (
                  <Tooltip
                    title="Eliminar"
                    arrow
                    // The editor lives inside UnifiedModal (z-index 100000)
                    slotProps={{ popper: { sx: { zIndex: 100001 } } }}
                  >
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        removePersona(index);
                      }}
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        color: "#d32f2f",
                        padding: "6px",
                        borderRadius: "20px",
                        "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.1)" },
                      }}
                    >
                      <Trash2 size={16} />
                    </Box>
                  </Tooltip>
                )}
                <ChevronDown
                  size={18}
                  style={{
                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                    color: theme.palette.text.secondary,
                  }}
                />
              </Box>
            </Box>

            {isExpanded && (
              <Box sx={{ px: 2, pb: 2 }}>
                <Separator />
                <Box sx={{ mt: 2 }}>
                  <PersonaFields
                    idPrefix={`persona-${index}`}
                    persona={persona}
                    onFieldChange={(field, value) => updatePersona(index, field, value)}
                  />
                </Box>
              </Box>
            )}
          </Box>
        );
      })}

      {disabled ? null : draft ? (
        <Box
          sx={{
            p: 3,
            borderRadius: "12px",
            border: `2px dashed ${colors.primary.main}`,
            backgroundColor: alpha(colors.primary.main, 0.02),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Plus size={18} color={colors.primary.main} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Nueva Buyer Persona
            </Typography>
          </Box>
          <PersonaFields
            idPrefix="new-persona"
            persona={draft}
            onFieldChange={(field, value) => setDraft((prev) => ({ ...prev, [field]: value }))}
          />
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
            <Button variant="outline" onClick={() => setDraft(null)}>
              Cancelar
            </Button>
            <Button onClick={confirmDraft} disabled={!isPersonaValid(draft)}>
              <Check size={16} style={{ marginRight: 8 }} />
              Confirmar
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          onClick={() => {
            setDraft(EMPTY_PERSONA);
            setExpandedIndex(null);
          }}
          sx={{
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: "8px",
            padding: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: colors.primary.main,
              backgroundColor: `${colors.primary.main}05`,
            },
          }}
        >
          <Plus size={18} style={{ color: theme.palette.text.secondary }} />
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Agregar Buyer Persona
          </Typography>
        </Box>
      )}
    </Box>
  );
}
